import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { DashboardStatistics } from "../types/dashboard.type";
import type { KPILimit } from "../stores/kpi.store";
import {
  isReportSectionEnabled,
  type ReportPdfSectionId,
} from "../constants/reportCustomize.catalog";
import { fetchImageAsDataUrl } from "./reportImage.helpers";
import { isStatAvailable } from "../constants/dashboardStats.catalog";
import { isTrackableKpi, kpiAlertFires, kpiTargetValue } from "../constants/kpiTargets";

const ZERO_PLATFORM_LOGO_PATH = "/images/zero_light.png";
const REPORT_RGB = { r: 0, g: 82, b: 204 } as const;
const PAGE_MARGIN_MM = 16;
const CONTENT_START_COMPACT_HEADER_MM = 52;
// Both logos share an identical slot (same height + width) and are scaled to
// fit inside it while preserving their natural aspect ratio, mirroring the ESG
// report header (see esg/src/components/report/ReportChrome.tsx).
const HEADER_LOGO_SLOT_HEIGHT_MM = 16;
const HEADER_LOGO_SLOT_WIDTH_MM = 30;
const HEADER_LOGO_GAP_MM = 3.7;
const HEADER_BOTTOM_LINE_Y_MM = 42;
const HEADER_META_ORG_NAME_Y_MM = 16;
const HEADER_META_PERIOD_Y_MM = 22;
const HEADER_META_GENERATED_Y_MM = 28;
const HEADER_META_ORG_CAP_HEIGHT_MM = 3.5;
const HEADER_META_BODY_DESCENT_MM = 1;
const HEADER_META_BLOCK_TOP_MM = HEADER_META_ORG_NAME_Y_MM - HEADER_META_ORG_CAP_HEIGHT_MM;
const HEADER_META_BLOCK_BOTTOM_MM = HEADER_META_GENERATED_Y_MM + HEADER_META_BODY_DESCENT_MM;
const HEADER_META_BLOCK_CENTER_MM =
  (HEADER_META_BLOCK_TOP_MM + HEADER_META_BLOCK_BOTTOM_MM) / 2;
const HEADER_LOGO_Y_MM = HEADER_META_BLOCK_CENTER_MM - HEADER_LOGO_SLOT_HEIGHT_MM / 2;

const MUTED_RGB = { r: 100, g: 116, b: 139 } as const;
const ORG_NAME_RGB = { r: 51, g: 65, b: 85 } as const;
const BORDER_RGB = { r: 226, g: 232, b: 240 } as const;

export const DASHBOARD_REPORT_KPI_UNITS: Record<string, string> = {
  total_orders: "orders",
  pending_orders: "orders",
  completed_orders: "orders",
  cancelled_orders: "orders",
  total_revenue: "SAR",
  average_incentive_per_kg: "SAR/kg",
  full_percentage: "%",
  completion_rate: "%",
  cancellation_rate: "%",
  waste_diversion_rate: "%",
  quality_compliance_rate: "%",
  rejected_waste_percentage: "%",
  storage_capacity_utilization: "%",
  processing_capacity_utilization: "%",
  total_waste_amount: "kg",
  completed_waste_amount: "kg",
  sustainable_supply: "kg",
  recycler_appetite: "kg",
  average_order_amount: "kg",
  in_transit_orders: "orders",
  orders_today: "orders",
  total_containers: "containers",
  full_containers: "containers",
  average_processing_time_hours: "hours",
};

/**
 * Report labels where the API field name would mislead (the PDF is English
 * only). Fields not listed fall back to a title-cased field name.
 */
const DASHBOARD_REPORT_KPI_LABELS: Record<string, string> = {
  waste_diversion_rate: "Completion Rate (by Mass)",
  completion_rate: "Completion Rate (by Orders)",
  total_waste_amount: "Waste Requested",
  completed_waste_amount: "Waste Collected",
  sustainable_supply: "Waste Received",
  recycler_appetite: "Waste Requested",
  average_order_amount: "Average Order Size",
  average_incentive_per_kg: "Average Revenue per kg",
  orders_today: "Orders Created Today",
  in_transit_orders: "In-transit Orders",
  full_percentage: "Containers with Full Alarm",
  average_processing_time_hours: "Average Order Processing Time",
};

/**
 * Fields left out of "Key Metrics": hard-coded zeros, duplicates of another
 * row (average_container_fill_rate = average_order_amount, trips_today_count =
 * orders_today) and figures the API computes inconsistently (mixed capacity
 * units, double-counted alarms).
 */
const DASHBOARD_REPORT_SKIPPED_KEYS = new Set([
  "average_container_fill_rate",
  "trips_today_count",
  "active_containers",
  "capacity_utilization_rate",
  "containers_needing_attention",
  "total_revenue_last_month",
]);

export interface BuildDashboardReportPdfParams {
  statistics: DashboardStatistics;
  organizationName: string;
  organizationLogoUrl: string | null;
  dashboardViewTab: number;
  selectedMonth: string;
  limits: KPILimit[];
  /** When omitted, every PDF section is included (legacy default). */
  includeSections?: ReportPdfSectionId[];
}

function imageFormat(dataUrl: string): "PNG" | "JPEG" {
  return dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG";
}

/**
 * Draws a logo inside a fixed slot, scaled to fit while preserving its natural
 * aspect ratio (equivalent to CSS `object-fit: contain`). `align` controls the
 * horizontal placement of the scaled image within the slot.
 */
function drawContainedLogo(
  doc: jsPDF,
  dataUrl: string,
  slotX: number,
  slotY: number,
  slotW: number,
  slotH: number,
  align: "start" | "end"
) {
  const props = doc.getImageProperties(dataUrl);
  const ratio = props.width / props.height;

  let drawH = slotH;
  let drawW = drawH * ratio;
  if (drawW > slotW) {
    drawW = slotW;
    drawH = drawW / ratio;
  }

  const drawY = slotY + (slotH - drawH) / 2;
  const drawX = align === "end" ? slotX + (slotW - drawW) : slotX;
  doc.addImage(dataUrl, imageFormat(dataUrl), drawX, drawY, drawW, drawH);
}

function drawHeaderLogos(
  doc: jsPDF,
  margin: number,
  logoY: number,
  slotH: number,
  slotW: number,
  platformLogoDataUrl: string | null,
  orgLogoDataUrl: string | null,
  includeDivider: boolean
) {
  let cursorX = margin;

  if (platformLogoDataUrl) {
    drawContainedLogo(doc, platformLogoDataUrl, cursorX, logoY, slotW, slotH, "end");
    cursorX += slotW + HEADER_LOGO_GAP_MM;
  }

  if (orgLogoDataUrl) {
    if (includeDivider && platformLogoDataUrl) {
      doc.setDrawColor(BORDER_RGB.r, BORDER_RGB.g, BORDER_RGB.b);
      doc.setLineWidth(0.25);
      doc.line(cursorX, logoY, cursorX, logoY + slotH);
      cursorX += HEADER_LOGO_GAP_MM;
    }
    drawContainedLogo(doc, orgLogoDataUrl, cursorX, logoY, slotW, slotH, "start");
  }
}

function drawReportHeader(
  doc: jsPDF,
  pageWidth: number,
  margin: number,
  platformLogoDataUrl: string | null,
  orgLogoDataUrl: string | null,
  organizationName: string,
  periodLabel: string,
  generatedLabel: string
) {
  const metaX = pageWidth - margin;

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(0, HEADER_BOTTOM_LINE_Y_MM, pageWidth, HEADER_BOTTOM_LINE_Y_MM);

  drawHeaderLogos(
    doc,
    margin,
    HEADER_LOGO_Y_MM,
    HEADER_LOGO_SLOT_HEIGHT_MM,
    HEADER_LOGO_SLOT_WIDTH_MM,
    platformLogoDataUrl,
    orgLogoDataUrl,
    true
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(ORG_NAME_RGB.r, ORG_NAME_RGB.g, ORG_NAME_RGB.b);
  doc.text(organizationName, metaX, HEADER_META_ORG_NAME_Y_MM, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(MUTED_RGB.r, MUTED_RGB.g, MUTED_RGB.b);
  doc.text(`Reporting period: ${periodLabel}`, metaX, HEADER_META_PERIOD_Y_MM, { align: "right" });
  doc.text(generatedLabel, metaX, HEADER_META_GENERATED_Y_MM, { align: "right" });
}

function ensureVerticalSpace(
  doc: jsPDF,
  y: number,
  threshold: number,
  drawHeader: () => void
): number {
  if (y <= threshold) return y;
  doc.addPage();
  drawHeader();
  return CONTENT_START_COMPACT_HEADER_MM;
}

function drawPageFooters(doc: jsPDF, pageWidth: number, margin: number) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFillColor(REPORT_RGB.r, REPORT_RGB.g, REPORT_RGB.b);
    doc.rect(0, pageH - 12, pageWidth, 12, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("RECYCLEE  •  Operational Report", margin, pageH - 4);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageH - 4, { align: "right" });
  }
}

export async function buildDashboardReportPdf({
  statistics,
  organizationName,
  organizationLogoUrl,
  dashboardViewTab,
  selectedMonth,
  limits,
  includeSections,
}: BuildDashboardReportPdfParams): Promise<{ doc: jsPDF; filename: string }> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = PAGE_MARGIN_MM;
  const rgb = REPORT_RGB;

  const platformLogoUrl = new URL(ZERO_PLATFORM_LOGO_PATH, window.location.origin).href;
  const [platformLogoDataUrl, orgLogoDataUrl] = await Promise.all([
    fetchImageAsDataUrl(platformLogoUrl),
    organizationLogoUrl?.trim()
      ? fetchImageAsDataUrl(organizationLogoUrl.trim())
      : Promise.resolve(null),
  ]);

  const orgName = organizationName || "Organization";
  const periodLabel =
    dashboardViewTab === 0
      ? format(new Date(selectedMonth), "MMMM yyyy")
      : "Organization Overview (All-time)";
  const generatedLabel = `Generated ${format(new Date(), "dd MMM yyyy, HH:mm")}`;

  const drawContinuationHeader = () =>
    drawReportHeader(
      doc,
      pageWidth,
      margin,
      platformLogoDataUrl,
      orgLogoDataUrl,
      orgName,
      periodLabel,
      generatedLabel
    );

  drawContinuationHeader();
  let y = CONTENT_START_COMPACT_HEADER_MM;

  const statsObj = statistics as unknown as Record<string, unknown>;
  const numericEntries: [string, number, number | undefined][] = [];

  for (const key of Object.keys(statsObj)) {
    if (key.startsWith("previous_month_")) continue;
    if (key === "containers" || key === "active_trucks") continue;
    if (DASHBOARD_REPORT_SKIPPED_KEYS.has(key) || !isStatAvailable(key)) continue;
    const val = statsObj[key];
    if (typeof val !== "number" || val === 0) continue;
    const prevKey = `previous_month_${key}`;
    const prev = typeof statsObj[prevKey] === "number" ? (statsObj[prevKey] as number) : undefined;
    numericEntries.push([key, val, prev]);
  }

  if (
    isReportSectionEnabled("key_metrics", includeSections) &&
    numericEntries.length > 0
  ) {
    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Key Metrics", margin, y);
    y += 2;

    const metricsBody = numericEntries.map(([key, val, prev]) => {
      const label =
        DASHBOARD_REPORT_KPI_LABELS[key] ??
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      const unit = DASHBOARD_REPORT_KPI_UNITS[key] ?? "";
      const formatted = `${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ""}`;
      let change = "—";
      if (prev !== undefined && prev !== 0 && dashboardViewTab === 0) {
        const pct = ((val - prev) / prev) * 100;
        change = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
      }
      return dashboardViewTab === 0 ? [label, formatted, change] : [label, formatted];
    });

    autoTable(doc, {
      startY: y,
      head: [
        dashboardViewTab === 0 ? ["Metric", "Value", "MoM Change"] : ["Metric", "Value"],
      ],
      body: metricsBody,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { cellPadding: 3 },
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  const wasteBreakdown = statistics.waste_type_material_breakdown?.filter((w) => w.total_kg > 0);
  if (
    isReportSectionEnabled("waste_type_breakdown", includeSections) &&
    wasteBreakdown &&
    wasteBreakdown.length > 0
  ) {
    y = ensureVerticalSpace(doc, y, 240, drawContinuationHeader);

    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Waste Type Breakdown", margin, y);
    y += 2;

    const total = wasteBreakdown.reduce((s, w) => s + w.total_kg, 0);
    const wasteBody = wasteBreakdown.map((w) => [
      w.waste_type,
      `${w.total_kg.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg`,
      `${((w.total_kg / total) * 100).toFixed(1)}%`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Waste Type", "Amount", "Share"]],
      body: wasteBody,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { cellPadding: 3 },
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  const incomingWaste = statistics.incoming_waste_breakdown?.filter((w) => w.amount > 0);
  if (
    isReportSectionEnabled("outgoing_waste_breakdown", includeSections) &&
    incomingWaste &&
    incomingWaste.length > 0
  ) {
    y = ensureVerticalSpace(doc, y, 220, drawContinuationHeader);

    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Pickup Requests by Day", margin, y);
    y += 2;

    const incomingBody = incomingWaste.map((w) => [
      w.date,
      w.waste_type,
      `${w.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Date", "Waste Type", "Amount"]],
      body: incomingBody,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { cellPadding: 3 },
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  const supplyPerRecycler = statistics.supply_per_recycler?.filter((r) => r.supply_amount > 0);
  if (
    isReportSectionEnabled("supply_per_recycler", includeSections) &&
    supplyPerRecycler &&
    supplyPerRecycler.length > 0
  ) {
    y = ensureVerticalSpace(doc, y, 220, drawContinuationHeader);

    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Supply Per Recycler", margin, y);
    y += 2;

    const recyclerBody = supplyPerRecycler.map((r) => [
      r.recycler_name,
      `${r.supply_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Recycler", "Supply Amount"]],
      body: recyclerBody,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { cellPadding: 3 },
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  const containers = statistics.containers?.filter((c) => c.status != null);
  if (
    isReportSectionEnabled("container_status", includeSections) &&
    containers &&
    containers.length > 0
  ) {
    y = ensureVerticalSpace(doc, y, 220, drawContinuationHeader);

    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Container Status Summary", margin, y);
    y += 2;

    const statusCounts: Record<string, number> = {};
    containers.forEach((c) => {
      const s = c.status || "unknown";
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    });

    const containerBody = Object.entries(statusCounts)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => [
        status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        count.toString(),
      ]);

    autoTable(doc, {
      startY: y,
      head: [["Status", "Count"]],
      body: containerBody,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { cellPadding: 3 },
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  const enabledKPIs = limits.filter((l) => l.isEnabled && isTrackableKpi(l.name));
  if (
    isReportSectionEnabled("kpi_targets", includeSections) &&
    enabledKPIs.length > 0
  ) {
    y = ensureVerticalSpace(doc, y, 220, drawContinuationHeader);

    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("KPI Targets vs Actual", margin, y);
    y += 2;

    // A limit reads "alert when <KPI> <operator> <value>" (as in the KPI dialog).
    const kpiBody = enabledKPIs.map((kpi) => {
      const actual = kpiTargetValue(statsObj, kpi.name);
      const unit = DASHBOARD_REPORT_KPI_UNITS[kpi.name.toLowerCase()] ?? "";
      const withUnit = (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ""}`;
      const alerting = kpiAlertFires(actual, kpi.operator, kpi.value);
      return [
        kpi.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        `${kpi.operator} ${withUnit(kpi.value)}`,
        actual !== null ? withUnit(actual) : "—",
        alerting === null ? "No data" : alerting ? "Alert" : "Within limit",
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [["KPI", "Alert when", "Actual", "Status"]],
      body: kpiBody,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { cellPadding: 3 },
    });
  }

  drawPageFooters(doc, pageWidth, margin);

  const filename =
    dashboardViewTab === 0
      ? `RECYCLEE_Report_${format(new Date(selectedMonth), "yyyy-MM")}.pdf`
      : `RECYCLEE_Report_Overview_${format(new Date(), "yyyy-MM-dd")}.pdf`;

  return { doc, filename };
}
